import React from "react";
import { Icon } from "../../ui/UIElements/Icon";
import { ContextHelp } from "../../ui";
import { convertTime, useGlobalUtils } from "../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
// import { getConversationIcon } from '.';

const toReadableLabel = (value) => {
  if (!value) return "";

  return String(value)
    .split(/[:_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" / ");
};

/**
 * ConversationCard component for displaying individual conversation information
 * @param {Object} props - Component props
 * @param {Object} props.conversation - Conversation data from API
 * @param {Function} props.onViewChat - Callback when view chat is clicked
 * @param {Function} props.onViewOrder - Callback when view order is clicked (if order exists)
 */
const ConversationCard = ({ conversation, onViewChat, onViewOrder }) => {
  const { t } = useTranslation("conversations");
  const { parseDate, parsePrice } = useGlobalUtils();

  const {
    status,
    status_reason,
    type,
    order,
    last_message_at,
    created_at,
    messages_count,
    user,
    usage,
    business_id,
    business,
    agent,
  } = conversation;

  // Get user display name
  const userName = user?.name;
  const userContact = user?.cellphone
    ? [
        user?.country_phone_code ? `+${user?.country_phone_code}` : "",
        user?.cellphone,
      ]
        .filter(Boolean)
        .join(" ")
    : user?.email;

  // Get status color and styling
  const getStatusStyle = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "writing":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  // Get status_reason color and styling
  const getStatusReasonStyle = (status_reason) => {
    switch (status_reason) {
      case "completed":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "abandoned":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "failed":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const orderId = order?.id;
  const orderTotal = order?.summary?.total || 0;
  const statusLabel = t(`status.${status}`, { defaultValue: status });
  const statusReasonLabel = status_reason
    ? t(`statusReason.${status_reason}`, {
        defaultValue: status_reason,
      })
    : "";
  const assignedBusinessName = business?.name;
  const assignedBusinessNameFallback =
    conversation?.business_name ||
    conversation?.businessName ||
    conversation?.assigned_business_name;
  const resolvedAssignedBusinessName =
    assignedBusinessName || assignedBusinessNameFallback;
  const assignedBusinessLabel = assignedBusinessName
    ? assignedBusinessName
    : resolvedAssignedBusinessName
      ? resolvedAssignedBusinessName
      : business_id
        ? t("card.notAvailable")
        : t("card.notAssigned");

  const rawAgentType =
    agent?.provider_type || agent?.type || agent?.front_type || type;
  const rawAgentProvider = agent?.provider;
  const formattedAgentType = toReadableLabel(rawAgentType);
  const formattedAgentProvider = toReadableLabel(rawAgentProvider);
  const agentTypeParts = [];

  [formattedAgentProvider, formattedAgentType].forEach((part) => {
    if (!part) return;

    const isDuplicate = agentTypeParts.some(
      (existingPart) => existingPart.toLowerCase() === part.toLowerCase(),
    );

    if (!isDuplicate) {
      agentTypeParts.push(part);
    }
  });

  const assignedAgentTypeLabel =
    agentTypeParts.join(" / ") || t("card.notAssigned");

  const channelIcons = {
    whatsapp: {
      label: "WhatsApp",
      bg: "bg-green-500",
      svg: (
        <svg
          className="h-6 w-6 text-white"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
      ),
    },
    chat: {
      label: "Web Chat",
      bg: "bg-blue-500",
      svg: (
        <svg
          className="h-6 w-6 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
    },
    default: {
      label: "Web",
      bg: "bg-gray-500",
      svg: (
        <svg
          className="h-6 w-6 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  };

  const channelTypeMap = {
    whatsapp: "whatsapp",
    "whatsapp:chat": "whatsapp",
    "ordering:chat": "chat",
    "finitless:chat": "chat",
    ordering: "chat",
  };

  const getConversationIcon = (type) => {
    const channel = channelIcons[channelTypeMap[type]] || channelIcons.default;
    return (
      <div className="group/icon relative">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-lg ${channel.bg}`}
        >
          {channel.svg}
        </div>
        <div className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity group-hover/icon:opacity-100">
          {channel.label}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col justify-between rounded-lg border border-gray-800 bg-gray-900 p-4 transition-colors hover:border-gray-700">
      <div className="flex flex-col">
        {/* Header */}
        <div className="mb-3 flex items-start justify-between">
          <div className="flex min-w-0 flex-1 items-center space-x-3">
            <div className="flex-shrink-0 rounded-lg">
              {getConversationIcon(type)}
            </div>
            <div className="min-w-0 flex-1">
              {userName && (
                <h3 className="truncate font-medium text-white">{userName}</h3>
              )}
              {userContact && (
                <p className="truncate text-sm text-gray-400">{userContact}</p>
              )}
            </div>
          </div>
          <div className="ml-2 flex flex-col items-end space-y-1">
            <span
              className={`rounded-full border px-2 py-1 text-xs font-medium capitalize ${getStatusStyle(
                status,
              )}`}
            >
              {statusLabel}
            </span>
            {status_reason && (
              <span
                className={`rounded-full border px-2 py-1 text-xs font-medium capitalize ${getStatusReasonStyle(
                  status_reason,
                )}`}
              >
                {statusReasonLabel}
              </span>
            )}
          </div>
        </div>

        {/* Business Info */}
        {business && (
          <div className="mb-2.5">
            <div className="flex items-center space-x-2">
              {business.logo ? (
                <img
                  src={business.logo}
                  alt={business.name || t("card.business")}
                  className="h-5 w-5 flex-shrink-0 rounded object-cover"
                />
              ) : (
                <Icon
                  name="Store"
                  className="h-5 w-5 flex-shrink-0 text-gray-400"
                />
              )}
              <div className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-white sm:text-sm">
                  {business.name || t("card.notAvailable")}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3 rounded-lg border border-gray-800 bg-gray-800/40 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <p className="text-xs text-gray-500">
                {t("card.labels.assignedBusiness")}
              </p>
              <p
                className="truncate text-sm font-medium text-gray-200"
                title={assignedBusinessLabel}
              >
                {assignedBusinessLabel}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">
                {t("card.labels.agentType")}
              </p>
              <p
                className="truncate text-sm font-medium text-gray-200"
                title={assignedAgentTypeLabel}
              >
                {assignedAgentTypeLabel}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-3 items-center justify-between">
          {/* <div className="flex items-center space-x-1 mb-2">
            <Icon name="Bot" className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400 text-sm">{type}</span>
          </div> */}
          <div className="mb-2 flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-400">
                {parseDate(last_message_at || created_at, {
                  outputFormat: "dd/MM/yyyy HH:mm",
                })}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="MessageSquare" className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">
                {t("card.labels.messages")}: {messages_count || 0}
              </span>
            </div>
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            {!!usage?.average_response_time_ms && (
              <div className="flex items-center space-x-1">
                <Icon name="Clock" className="h-4 w-4 text-gray-400" />
                <span className="flex items-center gap-1 text-sm text-gray-400">
                  {t("card.labels.responseTime")}:{" "}
                  {convertTime(
                    usage?.average_response_time_ms || 0,
                    "ms",
                    "s",
                    { decimals: 1, includeUnit: true },
                  )}
                  <ContextHelp
                    className="h-3.5 w-3.5"
                    ariaLabel={t("help.card.responseTime.ariaLabel")}
                    whatIs={t("help.card.responseTime.whatIs")}
                    period={t("help.card.responseTime.period")}
                  />
                </span>
              </div>
            )}
            {status_reason === "completed" &&
              usage?.roi != null &&
              usage.roi >= 0 && (
                <div className="flex items-center space-x-1">
                  <Icon name="ChartBar" className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-400">
                    {t("card.labels.roiValue", { value: usage.roi.toFixed(2) })}
                  </span>
                </div>
              )}
            {usage?.cost != null && usage.cost >= 0 && (
              <div className="flex items-center space-x-1">
                <Icon name="DollarSign" className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-400">
                  {parsePrice(usage.cost)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Business Assignment Information */}
      {!business_id && (
        <div className="mb-3 rounded-lg border border-orange-500/30 bg-orange-500/10 p-3">
          <div className="flex items-center space-x-2">
            <Icon
              name="AlertCircle"
              className="h-4 w-4 flex-shrink-0 text-orange-400"
            />
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-orange-400">
                {t("card.businessNotAssigned")}
              </p>
              <ContextHelp
                className="h-3.5 w-3.5"
                ariaLabel={t("help.card.businessNotAssigned.ariaLabel")}
                whatIs={t("help.card.businessNotAssigned.whatIs")}
                note={t("help.card.businessNotAssigned.note")}
              />
            </div>
          </div>
        </div>
      )}
      {/* Order Information */}
      {orderId ? (
        <div className="mb-3 rounded-lg border border-green-500/30 bg-green-500/10 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-400">
                {t("card.orderCreated")}
              </p>
              <p className="text-sm text-gray-300">
                {orderId
                  ? t("card.orderNumber", { id: orderId })
                  : t("card.orderCreated")}
                {orderTotal && ` - ${parsePrice(orderTotal)}`}
              </p>
            </div>
            {onViewOrder && (
              <button
                onClick={() => onViewOrder(orderId)}
                className="rounded-lg bg-green-500/20 px-3 py-1 text-xs text-green-400 transition-colors hover:bg-green-500/30"
              >
                {t("card.actions.viewOrder")}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium text-yellow-400">
                  {t("card.orderIncomplete")}
                </p>
                <ContextHelp
                  className="h-3.5 w-3.5"
                  ariaLabel={t("help.card.orderIncomplete.ariaLabel")}
                  whatIs={t("help.card.orderIncomplete.whatIs")}
                  note={t("help.card.orderIncomplete.note")}
                />
              </div>
              <p className="text-sm text-gray-300">
                {t("card.noOrderCreated")}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* View Chat Button */}
      <button
        onClick={() => onViewChat(conversation)}
        className="flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-2 text-white transition-all duration-200 hover:from-blue-600 hover:to-purple-700"
      >
        <Icon name="MessageCircle" className="h-4 w-4" />
        <span>{t("card.actions.viewChat")}</span>
      </button>
    </div>
  );
};

export default ConversationCard;
